import * as UserAction from '@ori-events/user-action';
import { DateFormat, formatDate, useCulture } from '@ori/culture';
import useTestId from '@ori/testid-generator';
import type { FC } from 'react';
import { useMemo, useCallback } from 'react';

import { CLICK_CATALOGUE_DETAIL } from '../../../constants';
import { useCatalogues } from '../../../context';
import { useShopSection } from '../../../context/navigation';
import { useIsMobile } from '../../../hooks';
import { useTranslations } from '../../../hooks/useTranslations';
import { formatValidity } from '../../../utils/formatValidity';
import { SkeletonCatalogueDesktop, SkeletonCatalogueMobile } from '../../Skeleton';
import { CATALOGUES_SEE_ALL, CATALOGUES_SEE_ALL_LINK, CTA_TEXT } from '../../common';
import { CurrentCatalogue } from './CurrentCatalogue';
import { NextCatalogue } from './NextCatalogue';
import { CATALOGUES_ROOT } from './constant';
import { CallToAction, CallToActionWrapper, CatalogueDetailButton, CatalogueWrapper, CTAText, Root } from './styles';

export const Catalogues: FC = () => {
  const { catalogues, isLoading } = useCatalogues();
  const { items } = useShopSection();
  const isMobile = useIsMobile();
  const { getTestId } = useTestId();
  const { culture, timeZone } = useCulture();
  const { translations } = useTranslations();
  // istanbul ignore next -- just formatting
  const formatValidityDate = useCallback(
    (date: Date) => formatDate(date, DateFormat.Custom, culture, timeZone, { customPattern: 'dd/MM' }),
    [culture, timeZone],
  );
  const sendAnalyticsData = useCallback(() => {
    UserAction.dispatch(CLICK_CATALOGUE_DETAIL);
  }, []);

  const catalogueDataFromItems = useMemo(() => {
    const link = items.find((e) => e.linkType === 'Catalogue');
    const catalogueCurrent = link?.children.find((e) => e.linkType === 'DigitalCatalogueCurrent');
    const catalogueNext = link?.children.find((e) => e.linkType === 'DigitalCatalogueNext');
    const isCatalogueNextValid = Boolean(
      !isMobile && catalogueNext?.id && catalogueNext.imageUrl && catalogueNext.title && catalogueNext.url,
    );
    const linkToAllCatalogues = link?.children.find((e) => e.linkType === 'Catalogues')?.url;

    return { catalogueCurrent, catalogueNext, isCatalogueNextValid, linkToAllCatalogues };
  }, [items, isMobile]);

  if (isLoading) {
    return isMobile ? <SkeletonCatalogueMobile /> : <SkeletonCatalogueDesktop />;
  }

  if (items.length === 0) {
    return null;
  }

  const { catalogueCurrent, catalogueNext, isCatalogueNextValid, linkToAllCatalogues } = catalogueDataFromItems;

  if (!catalogueCurrent?.id || !catalogueCurrent.imageUrl || !catalogueCurrent.title || !catalogueCurrent.url) {
    return null;
  }

  return (
    <Root>
      <CatalogueWrapper data-testid={getTestId(CATALOGUES_ROOT)}>
        <CurrentCatalogue
          catalogue={{
            ...catalogueCurrent,
            validity: formatValidity(catalogueCurrent.id, catalogues, formatValidityDate),
          }}
          isMobile={isMobile}
          linkToAllCatalogues={linkToAllCatalogues}
          onClick={sendAnalyticsData}
        />

        {isCatalogueNextValid && catalogueNext ? (
          <NextCatalogue
            catalogue={{
              ...catalogueNext,
              validity: formatValidity(catalogueNext.id ?? '', catalogues, formatValidityDate),
            }}
            onClick={sendAnalyticsData}
          />
        ) : null}
      </CatalogueWrapper>

      {!isMobile && linkToAllCatalogues ? (
        <CallToActionWrapper>
          <CallToAction data-testid={getTestId(CATALOGUES_SEE_ALL)}>
            <CTAText
              data-testid={getTestId(CTA_TEXT)}
              variant="subtitle1"
            >
              {translations.callToActionCatalogueText}
            </CTAText>
            <CatalogueDetailButton
              fullWidth
              variant="outlined"
              data-testid={getTestId(CATALOGUES_SEE_ALL_LINK)}
              href={linkToAllCatalogues}
            >
              {translations.seeAllCatalogues}
            </CatalogueDetailButton>
          </CallToAction>
        </CallToActionWrapper>
      ) : null}
    </Root>
  );
};
